import { useEffect, useRef, useState } from "react";
import PageCTA from "@/components/PageCTA";
import VoiceLogRecorder from "@/components/VoiceLogRecorder";
import VoiceLogFeed from "@/components/VoiceLogFeed";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup, useMap, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { MapPin, Crosshair, Radio, RadioTower, X, Mic } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";

const CENTRE = [57.0, -4.2];
const ZOOM = 6;
const LIVE_REFRESH_MS = 30000;

function FlyTo({ target }) {
    const map = useMap();
    useEffect(() => {
        if (target) map.flyTo([target.lat, target.lng], 10, { duration: 1.2 });
    }, [target, map]);
    return null;
}

export default function MapPage() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [locations, setLocations] = useState([]);
    const [active, setActive] = useState(null);
    const [myLocation, setMyLocation] = useState(null);
    const [live, setLive] = useState(null); // {active, lat, lng, label, updated_at}
    const [broadcasting, setBroadcasting] = useState(false);
    const [voiceLogOpen, setVoiceLogOpen] = useState(false);
    const [voiceFeedTick, setVoiceFeedTick] = useState(0); // bump to refetch feed after submit
    const broadcastTimer = useRef(null);

    useEffect(() => {
        api.get("/map/locations").then((r) => setLocations(r.data));
    }, []);

    // Poll live location for everyone
    useEffect(() => {
        const fetchLive = () => api.get("/live/location").then((r) => setLive(r.data)).catch(() => {});
        fetchLive();
        const id = setInterval(fetchLive, LIVE_REFRESH_MS);
        return () => clearInterval(id);
    }, []);

    const showMyLocation = () => {
        if (!navigator.geolocation) {
            toast.error("Your browser doesn't support geolocation.");
            return;
        }
        toast("Asking your browser for permission…");
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setMyLocation(coords);
                setActive({ ...coords, title: "You", region: "" });
                toast.success("Found you.");
            },
            (err) => toast.error(err.message || "Couldn't get your location."),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const pushLocationToServer = async () => {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    try {
                        await api.post("/live/location", {
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude,
                            label: "Mark is here right now",
                        });
                        resolve();
                    } catch (e) { reject(e); }
                },
                (err) => reject(new Error(err.message || "Geolocation failed")),
                { enableHighAccuracy: true, timeout: 10000 }
            );
        });
    };

    const startBroadcast = async () => {
        if (!navigator.geolocation) {
            toast.error("Your browser doesn't support geolocation.");
            return;
        }
        try {
            await pushLocationToServer();
            toast.success("You're live. Visitors will see your location on the map.");
            setBroadcasting(true);
            broadcastTimer.current = setInterval(() => {
                pushLocationToServer().catch(() => {});
            }, LIVE_REFRESH_MS);
            // refresh local view immediately
            const { data } = await api.get("/live/location");
            setLive(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const stopBroadcast = async () => {
        if (broadcastTimer.current) clearInterval(broadcastTimer.current);
        broadcastTimer.current = null;
        setBroadcasting(false);
        try {
            await api.delete("/live/location");
            setLive({ active: false });
            toast.success("Broadcast stopped.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    useEffect(() => {
        return () => {
            if (broadcastTimer.current) clearInterval(broadcastTimer.current);
        };
    }, []);

    const pinColour = (kind) => kind === "visited" ? "#0055A4" : "#6ba7de";

    return (
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="map-page">
            <header className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 relative">
                <div className="lg:col-span-8">
                    <Reveal>
                        <div className="overline mb-5"><span className="editorial-rule" />Locations</div>
                    </Reveal>
                    <h1
                        className="font-serif font-light tracking-tighter leading-[0.9]"
                        style={{ fontSize: "clamp(2.8rem, 7vw, 7rem)" }}
                        data-testid="map-title"
                    >
                        <div><MaskedLine delay={0.1}>Every flight,</MaskedLine></div>
                        <div>
                            <MaskedLine delay={0.3}>
                                <span className="italic text-[var(--sb-brand)]">on a map.</span>
                            </MaskedLine>
                        </div>
                    </h1>
                </div>
                <Reveal delay={0.45} className="lg:col-span-4 self-end">
                    <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed">Bright pins mark filmed flights. Deeper pins are places I&rsquo;ve been. Switch the map style top-right.</p>
                </Reveal>
            </header>

            <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="map-actions">
                <button onClick={showMyLocation} className="btn-outline text-sm" style={{padding: "0.55rem 1.1rem"}} data-testid="show-my-location">
                    <Crosshair size={14} /> Show my location
                </button>
                {isAdmin && (broadcasting ? (
                    <button onClick={stopBroadcast} className="btn-primary text-sm" style={{padding: "0.55rem 1.1rem", background: "#9b3a3a"}} data-testid="stop-broadcast">
                        <X size={14} /> Stop broadcasting
                    </button>
                ) : (
                    <button onClick={startBroadcast} className="btn-primary text-sm" style={{padding: "0.55rem 1.1rem"}} data-testid="start-broadcast">
                        <Radio size={14} /> Broadcast my location
                    </button>
                ))}
                {live?.active && (
                    <div className="flex items-center gap-2 text-sm text-[var(--sb-brand)]" data-testid="live-indicator">
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--sb-brand)] opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--sb-brand)]"></span>
                        </span>
                        Mark is live
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8 relative card overflow-hidden" data-testid="scotland-map">
                    <div style={{height: "640px", width: "100%", background: "#0b1f3a"}}>
                        <MapContainer
                            center={CENTRE}
                            zoom={ZOOM}
                            minZoom={5}
                            maxZoom={18}
                            scrollWheelZoom
                            style={{height: "100%", width: "100%", background: "#0b1f3a"}}
                            attributionControl={true}
                        >
                            <LayersControl position="topright">
                                <LayersControl.BaseLayer checked name="Dark">
                                    <TileLayer
                                        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                        attribution='&copy; OpenStreetMap &copy; CARTO'
                                        subdomains="abcd"
                                        maxZoom={20}
                                    />
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer name="Satellite">
                                    <TileLayer
                                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                        attribution='Tiles &copy; Esri'
                                        maxZoom={19}
                                    />
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer name="Terrain">
                                    <TileLayer
                                        url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; OpenTopoMap &copy; OpenStreetMap'
                                        subdomains="abc"
                                        maxZoom={17}
                                    />
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer name="Streets">
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; OpenStreetMap contributors'
                                        subdomains="abc"
                                        maxZoom={19}
                                    />
                                </LayersControl.BaseLayer>
                            </LayersControl>

                            {locations.map((l) => (
                                <CircleMarker
                                    key={l.id}
                                    center={[l.lat, l.lng]}
                                    radius={active?.id === l.id ? 10 : 7}
                                    pathOptions={{
                                        color: pinColour(l.kind),
                                        fillColor: pinColour(l.kind),
                                        fillOpacity: active?.id === l.id ? 0.9 : 0.65,
                                        weight: 2,
                                    }}
                                    eventHandlers={{ click: () => setActive(l) }}
                                >
                                    <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                                        <span style={{fontFamily: "Cormorant Garamond, serif", fontSize: "14px"}}>{l.title}</span>
                                    </Tooltip>
                                    <Popup>
                                        <div style={{fontFamily: "Outfit, sans-serif"}}>
                                            <div style={{textTransform: "uppercase", letterSpacing: "0.18em", fontSize: "0.65rem", color: pinColour(l.kind), marginBottom: "0.25rem"}}>
                                                {l.kind === "visited" ? "Visited" : l.region}
                                            </div>
                                            <div style={{fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem"}}>{l.title}</div>
                                            {l.note && <div style={{marginTop: "0.4rem", fontSize: "0.85rem", color: "#b8c8de"}}>{l.note}</div>}
                                        </div>
                                    </Popup>
                                </CircleMarker>
                            ))}

                            {myLocation && (
                                <CircleMarker
                                    center={[myLocation.lat, myLocation.lng]}
                                    radius={9}
                                    pathOptions={{ color: "#0055A4", fillColor: "#0055A4", fillOpacity: 0.8, weight: 3 }}
                                >
                                    <Tooltip direction="top" offset={[0, -8]} permanent opacity={0.95}>
                                        <span style={{fontFamily: "Outfit, sans-serif", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.15em"}}>You</span>
                                    </Tooltip>
                                </CircleMarker>
                            )}

                            {live?.active && (
                                <CircleMarker
                                    center={[live.lat, live.lng]}
                                    radius={12}
                                    pathOptions={{ color: "#d4a574", fillColor: "#d4a574", fillOpacity: 0.85, weight: 3, className: "pin-pulse" }}
                                >
                                    <Tooltip direction="top" offset={[0, -10]} permanent opacity={0.95}>
                                        <span style={{fontFamily: "Cormorant Garamond, serif", fontSize: "14px", color: "#0b1f3a"}}>📡 {live.label || "Mark is here right now"}</span>
                                    </Tooltip>
                                </CircleMarker>
                            )}

                            <FlyTo target={active} />
                        </MapContainer>
                    </div>
                    <p className="text-xs text-[var(--sb-text-muted)] mt-3 italic text-center max-w-2xl mx-auto" data-testid="map-page-caption">
                        Interactive map of every flight and every place I&rsquo;ve been. Bright pins are filmed flights (tap to see the reel); deeper pins are places I&rsquo;ve travelled to. Use the layer control top-right to switch between satellite and terrain styles.
                    </p>
                </div>

                <div className="lg:col-span-4 space-y-3">
                    {active ? (
                        <div className="card overflow-hidden" data-testid="map-pin-detail">
                            {active.thumb && <img src={active.thumb} alt={active.title} className="w-full aspect-video object-cover" />}
                            <div className="p-6">
                                <div className="overline mb-2">{active.kind === "visited" ? "Visited" : (active.region || "")}</div>
                                <h3 className="font-serif text-2xl tracking-tight mb-3">{active.title}</h3>
                                {active.note && <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed mb-3">{active.note}</p>}
                                {active.kind === "video" && <a href={`/gallery`} className="text-sm text-[var(--sb-brand)] hover:text-[var(--sb-text)] inline-flex items-center gap-2">Open in gallery →</a>}
                            </div>
                        </div>
                    ) : (
                        <div className="card p-6 text-[var(--sb-text-dim)]">
                            <MapPin className="text-[var(--sb-brand)] mb-3" />
                            <p className="text-sm">Tap a pin — or use the buttons above to show your location.</p>
                        </div>
                    )}

                    {live?.active && (
                        <div className="card p-5 border-[var(--sb-brand)]" data-testid="live-card">
                            <div className="flex items-center gap-2 mb-2">
                                <RadioTower size={16} className="text-[var(--sb-brand)]" />
                                <div className="overline">Live now</div>
                            </div>
                            <p className="text-sm text-[var(--sb-text-dim)]">{live.label || "Mark is at a flight spot right now."}</p>
                            <button onClick={() => setActive({ id: "live", lat: live.lat, lng: live.lng, title: "Mark is here", region: "Live" })} className="mt-3 text-sm text-[var(--sb-brand)] hover:text-[var(--sb-text)]">Fly to live pin →</button>
                        </div>
                    )}

                    <div className="card p-4">
                        <div className="overline mb-3 flex items-center justify-between">
                            <span>All locations</span>
                            <span className="text-[var(--sb-text-muted)]">{locations.length}</span>
                        </div>
                        {locations.length === 0 ? (
                            <p className="text-sm text-[var(--sb-text-dim)] px-2 py-3">No flights logged yet.</p>
                        ) : (
                            <ul className="space-y-2 max-h-72 overflow-auto">
                                {locations.map((l) => (
                                    <li key={l.id}>
                                        <button onClick={() => setActive(l)} className="w-full text-left text-sm text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] flex justify-between" data-testid={`map-list-${l.id}`}>
                                            <span className="flex items-center gap-2">
                                                <span className="inline-block w-2 h-2 rounded-full" style={{background: pinColour(l.kind)}}></span>
                                                {l.title}
                                            </span>
                                            <span className="text-[var(--sb-text-muted)] text-xs uppercase tracking-wider">{l.kind === "visited" ? "Visited" : l.region}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            {/* -------- Visitor Voice Logs -------------------------------- */}
            <section className="mt-24 lg:mt-32" data-testid="voice-log-section">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
                    <Reveal className="lg:col-span-8">
                        <div className="overline mb-3" style={{ color: "#0055A4" }}>
                            <span className="editorial-rule" style={{ background: "#0055A4" }} />
                            Visitor Voice Logs
                        </div>
                        <h2
                            className="font-serif tracking-tight leading-[0.95]"
                            style={{ fontSize: "clamp(2rem, 4.4vw, 3.8rem)" }}
                        >
                            Send a message from<br/>
                            <span className="italic" style={{ color: "#0055A4" }}>a place you love.</span>
                        </h2>
                        <p className="text-[var(--sb-text-dim)] mt-5 max-w-xl text-lg leading-relaxed">
                            Record a short voice note about somewhere in Scotland that stayed with you. I&rsquo;ll review it, then it lives here on the map for future flyers to hear.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-4 flex lg:justify-end items-end">
                        <button
                            type="button"
                            onClick={() => setVoiceLogOpen(true)}
                            className="btn-primary"
                            data-testid="voice-log-open"
                        >
                            <Mic size={18} /> Leave a voice log
                        </button>
                    </Reveal>
                </div>

                <VoiceLogFeed key={voiceFeedTick} limit={6} />
            </section>

            <VoiceLogRecorder
                open={voiceLogOpen}
                onClose={() => setVoiceLogOpen(false)}
                onSubmitted={() => setVoiceFeedTick((t) => t + 1)}
            />

            <PageCTA variant="story" className="!px-0" />
            <HighlandBackdrop variant="soft" />
        </div>
    );
}
